import { twMerge } from "tailwind-merge";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H3,
  H6,
} from "@sellar-io/components";
import type { DeliveryChargeRuleListResponseType } from "@sellar-io/api-v2/types";
import type { CalculatedDeliveryOffer } from "@sellar-io/common";
import {
  formatCurrency,
  generateContainerTypeDeliveryChargeRulesText,
} from "@sellar-io/common";
import { useState } from "react";
import { Icon } from "../../../../components";
import { DeliveryChargeCapToolTip } from "../../../../components/deliveryChargeToolTip/DeliveryChargeCapToolTip";
import { BasicDialog } from "../../../../components/modal/BasicDialog";

import { CustomOrderDeliveryChargeDialog } from "../dialogs/CustomOrderDeliveryChargeDialog";

const QualifyingDeliveryOffersList = ({
  deliveryOffers,
}: {
  deliveryOffers: CalculatedDeliveryOffer[];
}) => {
  return (
    <>
      {deliveryOffers.map((offer) => {
        return (
          <H6 key={offer.deliveryRule.id}>
            {offer.qualifiesForFreeDelivery && (
              <>
                {generateContainerTypeDeliveryChargeRulesText(
                  offer.deliveryRule,
                )}
              </>
            )}
          </H6>
        );
      })}
    </>
  );
};

export const DeliveryCard = ({
  customDeliveryCharge,
  setCustomDeliveryCharge,
  deliveryCharge,
  deliveryChargeRule,
  calculatedContainerTypeDeliveryOffers,
  flatDeliveryCharge,
  orderQualifiesDeliveryChargeRule,
}: {
  customDeliveryCharge: number | undefined;
  setCustomDeliveryCharge: (deliveryCharge: number | undefined) => void;
  deliveryCharge: { charge: number; isDeliveryChargeCapped: boolean };
  deliveryChargeRule:
    | Pick<DeliveryChargeRuleListResponseType[number], "value">
    | undefined;
  calculatedContainerTypeDeliveryOffers: CalculatedDeliveryOffer[];
  flatDeliveryCharge: number;
  orderQualifiesDeliveryChargeRule: boolean;
}) => {
  const [deliveryChargeModalActive, setDeliveryChargeModalActive] =
    useState(false);

  const [discountInformationModalActive, setDiscountInformationModalActive] =
    useState(false);

  const freeDeliveryThreshold = deliveryChargeRule?.value;
  return (
    <Card className="bg-white">
      <CardHeader
        className={twMerge("bg-white", "flex items-center justify-between p-4")}
      >
        <H3>Delivery</H3>
        <Button
          size={"small"}
          color={"transparent"}
          onClick={() => setDiscountInformationModalActive(true)}
        >
          <Icon icon={"INFORMATION_OUTLINE"} />
        </Button>
      </CardHeader>
      <CardContent className={"divide-y p-0 *:p-4"}>
        <div
          className={
            Number.isInteger(customDeliveryCharge)
              ? "line-through opacity-60"
              : ""
          }
        >
          <div className="flex cursor-pointer items-center justify-between pb-2">
            <div>Auto-calculated charges</div>
            {deliveryCharge.isDeliveryChargeCapped &&
            deliveryCharge.charge > 0 ? (
              <DeliveryChargeCapToolTip>
                <div
                  className={
                    "inline-flex cursor-pointer items-center gap-1 font-bold"
                  }
                >
                  <Icon icon={"INFORMATION_OUTLINE"} size={0.65} />
                  {formatCurrency(deliveryCharge.charge)}
                </div>
              </DeliveryChargeCapToolTip>
            ) : (
              <div>
                {deliveryCharge.charge === 0
                  ? "FREE"
                  : formatCurrency(deliveryCharge.charge)}
              </div>
            )}
          </div>

          {typeof freeDeliveryThreshold === "number" && (
            <H6>
              Free delivery threshold: {formatCurrency(freeDeliveryThreshold)}
            </H6>
          )}

          {!orderQualifiesDeliveryChargeRule && (
            <>
              {!!flatDeliveryCharge && (
                <H6>
                  Flat delivery charge: {formatCurrency(flatDeliveryCharge)}
                </H6>
              )}

              <QualifyingDeliveryOffersList
                deliveryOffers={calculatedContainerTypeDeliveryOffers}
              />
            </>
          )}
        </div>
        {!!customDeliveryCharge ? (
          <div
            className={"flex cursor-pointer justify-between"}
            onClick={() => setDeliveryChargeModalActive(true)}
          >
            <div>Custom charge</div>
            <div className={"flex items-center gap-2 self-start"}>
              {formatCurrency(customDeliveryCharge)}
              <Icon icon={"PENCIL"} />
            </div>
          </div>
        ) : (
          <div
            className={"flex cursor-pointer items-center gap-1"}
            onClick={() => setDeliveryChargeModalActive(true)}
          >
            <Icon icon={"PLUS_CIRCLE"} /> Add custom charge
          </div>
        )}
        <CustomOrderDeliveryChargeDialog
          active={deliveryChargeModalActive}
          onDismiss={() => setDeliveryChargeModalActive(false)}
          onSubmit={(deliveryCharge) => {
            setDeliveryChargeModalActive(false);
            setTimeout(() => {
              setCustomDeliveryCharge(deliveryCharge);
            }, 200);
          }}
          onDelete={() => {
            setDeliveryChargeModalActive(false);
            setTimeout(() => {
              setCustomDeliveryCharge(undefined);
            }, 200);
          }}
        />
        <BasicDialog
          dialogActive={discountInformationModalActive}
          onDismiss={() => setDiscountInformationModalActive(false)}
          title={"About delivery"}
          description="Any individual product item charges from your delivery tiers will be automatically calculated and applied. You can override these charges by setting your own custom delivery charge."
        />
      </CardContent>
    </Card>
  );
};
